import { Field } from 'formik';
import React from 'react';

import { getPartnerTranslation } from 'Utilities/helpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';

import { SCOPE_TYPE, SCOPE_TYPE_READ } from 'Constants/scopeTypes';

import ToggleField from 'Components/input/ToggleField';

import infoIcon from 'Images/icons/info-icon.svg';

/**
 * @param {string} scopeType consent type eg. read or write
 * @param {string[]} scopes  all the items that are shared under this scope
 * @param {ScopeDetails} scopeDetails scope details object from partner
 * @param {string} partnerName name of the partner
 * @param {string} partnerType type of partner, mainly used for getting specific translations for a partner type if it exists
 * @returns a component that renders the sharing information for a particular scope
 */

type Props = {
  scopeType: SCOPE_TYPE;
  scopes: string[];
  scopeDetails?: ScopeDetails;
  partnerName: string;
  partnerType: string;
};

const scopeInfoClick = (scope: string, scopeDetails: ScopeDetails) => {
  mediator.publish('partner:consentInfo', { scope, scopeDetails });
};

const getSectionTitle = ({
  scopeType,
  scopes,
  partnerName,
  partnerType,
}: Pick<Props, 'scopeType' | 'scopes' | 'partnerName' | 'partnerType'>): string => {
  const includesHealthData = scopes.includes(`${scopeType}.healthdata`);

  let translationKey = includesHealthData ? 'receiveDataFromPartner' : 'receivedData';

  if (scopeType === SCOPE_TYPE_READ) {
    translationKey = includesHealthData ? 'shareDataWithPartner' : 'requestingAccess';
  }

  return getPartnerTranslation('Consent', partnerType, translationKey, { partnerName });
};

const ScopeSection = ({
  scopeType,
  scopes,
  scopeDetails,
  partnerName,
  partnerType,
}: Props): JSX.Element | null => {
  if (scopes.length === 0 || !scopeDetails) {
    return null;
  }

  const sectionTitle = getSectionTitle({
    scopeType,
    scopes,
    partnerName,
    partnerType,
  });

  return (
    <div>
      <div className="d-flex flex-row align-items-start">
        <div className="pr-2">
          <Field
            component={ToggleField}
            disabled={false}
            id={`${scopeType}-toggle`}
            name={`${scopeType}Toggle`}
            onValuesChanged={() => undefined}
            readOnly={false}
          />
        </div>
        <div className="font-weight-bold text-lg pt-1" id={`${scopeType}Scopes-title`}>
          {sectionTitle}
        </div>
      </div>
      <div className="pl-1">
        {scopes.map((scope) => (
          <div key={scope} className="mt-2 pt-1" id={`${scopeType}Scope-${scope}`}>
            <span id={`${scopeType}Scope-${scope}-text`}>
              {i18n.t(`Scopes.${scope.replace(/\./g, '_')}ScopeName`)}
            </span>
            <button
              className="btn btn-sm btn-round border-0 ml-2"
              id={`${scopeType}Scope-${scope}-info-btn`}
              onClick={() => scopeInfoClick(scope, scopeDetails)}
              type="button"
            >
              <img src={infoIcon} alt="info" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScopeSection;
